import { Box } from '@zillow/constellation-10';
import React from 'react';

import type { AuthorPageContentProps } from '../../util/buildPrimaryAuthorPage';

import AuthorHeader from './AuthorHeader';
import AuthorMainContent from './AuthorMainContent';
import type { AuthorSocialLinks } from './AuthorPage.types';
import AuthorSidebar from './AuthorSidebar';

/**
 * The maximum width for the outer AuthorPage container.
 */
const AUTHOR_PAGE_MAX_WIDTH = '2560px';

/**
 * The maximum width for the main content row inside AuthorPage.
 */
export const AUTHOR_PAGE_CONTENT_MAX_WIDTH = '1280px';

/**
 * AuthorPage component for displaying author details and posts.
 *
 * @param sidebarTop - The CSS top value for the sticky sidebar (e.g., 'layout.looser', '80px').
 */
const AuthorPage: React.FC<
    Omit<AuthorPageContentProps, 'total'> & { sidebarTop?: string }
> = ({ author, posts, pages, initialPage, sidebarTop }) => {
    // Type guard for socialLinks
    const socialLinks: AuthorSocialLinks | undefined =
        typeof author.socialLinks === 'object' && author.socialLinks !== null
            ? (author.socialLinks as AuthorSocialLinks)
            : undefined;

    return (
        <Box
            css={{
                maxWidth: AUTHOR_PAGE_MAX_WIDTH,
                marginBlock: '0',
                marginInline: 'auto',
            }}
        >
            {/* Header */}
            <AuthorHeader author={author} socialLinks={socialLinks} />

            {/* Content Row */}
            {/* v8 spacing md (24px) → loose, xl (48px) → layout.looser,
                xxl (64px, CMS theme) → layout.vast.default.
                v8 '@media $lg'/'$xl' (min-width 769px/1024px) → Panda lg/xl. */}
            <Box
                css={{
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    zIndex: 1,
                    paddingInline: 'loose',
                    gap: 'layout.vast.default',
                    marginBlock: '0',
                    marginInline: 'auto',
                    justifyContent: 'center',
                    maxWidth: AUTHOR_PAGE_CONTENT_MAX_WIDTH,
                    lg: {
                        flexDirection: 'row',
                        paddingInline: 'layout.looser',
                    },
                    xl: {
                        paddingInline: 'layout.vast.default',
                    },
                }}
            >
                {/* Sidebar - hidden below desktop */}
                <AuthorSidebar author={author} sidebarTop={sidebarTop} />

                {/* Main Content */}
                <AuthorMainContent
                    author={author}
                    posts={posts}
                    pages={pages}
                    initialPage={initialPage}
                />
            </Box>
        </Box>
    );
};

export default AuthorPage;
